import { useQuery } from '@tanstack/react-query';
import instance from '@api/axiosInstance';
import { cloneDeep } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { message, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { dashboardsSelector as tempDashboardsSelector } from '@store/tempDashboard/selectors';
import { getAppViewerModuleTempDashboardUrl } from '@src/components/common/Dashboard/utils/getTempDashboardUrl';
import { StyledButton, TableCellWithCopy } from '../styled';
import copy from 'copy-to-clipboard';
import { DATE_SHORTCUTS, QueryViewId } from '@src/utils/constants';
import { calcShortcutInterval, uuidv4 } from '@src/utils';
import { genGlobalFiltersLocalstorageKey } from '@src/pages/AppTimeline/useGlobalFilters';
import localStorage from '@src/utils/localStorage';
import { filterSelector, intervalSelector } from '@src/store/common/selectors';
import moment from 'moment-timezone';
import SearchTool from '@src/components/app-viewer-sessions/Table/SearchTool';
import { ECO_RESERVED_DIMENSION_INFOS } from '@src/components/app-viewer-sessions/utils';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

export const useGetSampledData = (appliedDimensions, filter) => {
  const dimensions = cloneDeep(appliedDimensions).sort((a, b) => a.localeCompare(b));
  const trendsInterval = useSelector(intervalSelector);

  // Calculate the interval based on trends interval duration
  const interval = useMemo(() => {
    const from = moment(trendsInterval.from);
    const to = moment(trendsInterval.to);
    const diffInHours = to.diff(from, 'hours');
    const now = moment();
    const hoursFromNow = now.diff(to, 'hours');

    if (diffInHours > 24 || hoursFromNow > 24) {
      // If trends interval is greater than 24 hours or interval is older than 24 hours, use last 24 hours
      return calcShortcutInterval(DATE_SHORTCUTS.past24Hours);
    } else {
      // If trends interval is 24 hours or less and within last 24 hours, use the trends interval
      return [from.toDate(), to.toDate()];
    }
  }, [trendsInterval]);

  const payload = {
    queries: [
      {
        type: 'group-by',
        dataset: 'AppExperience',
        interval: interval[0].toISOString() + '/' + interval[1].toISOString(),
        granularity: 'ALL',
        filter: filter,
        limit: 200,
        extraFields: dimensions.map((dim) => `event.${dim}`),
        sortBy: [],
        options: {
          withTotals: true,
          context: {
            view_id: QueryViewId.HIGH_CARDINALITY_SAMPLED_DATA,
          },
        },
      },
    ],
  };
  return useQuery(
    ['sampledData', dimensions, filter, interval],
    async () => {
      const response = await instance.post(`/metrics/sampled-event/`, payload);
      if (!response.data) {
        throw new Error('Network response was not ok');
      }
      return response?.data[0]?.data;
    },
    {
      enabled: !!dimensions,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  );
};

export const useGetDropdownOption = (dimensionList, defaultDimensions) => {
  const dimensionMap = useMemo(() => {
    const categories = {};
    categories['Mandatory'] = defaultDimensions.map((dim) => ({
      name:
        ECO_RESERVED_DIMENSION_INFOS[dim]?.name ||
        dimensionList.find((d) => d.groupBy === dim)?.name ||
        dim,
      metric: dim,
    }));
    dimensionList.forEach((dim) => {
      const categoryKey = dim.category.charAt(0).toUpperCase() + dim.category.slice(1);
      if (!categories[categoryKey]) {
        categories[categoryKey] = [];
      }
      if (defaultDimensions.includes(dim.groupBy) || dim.highCardinality) {
        return;
      }
      categories[categoryKey].push({
        name: ECO_RESERVED_DIMENSION_INFOS[dim]?.name || dim.name,
        metric: dim.groupBy,
      });
    });
    return categories;
  }, [dimensionList, defaultDimensions]);

  return dimensionMap;
};

export const useGetColumnConfig = ({
  dimensionList,
  selectedDimensions,
  anchoredDimensions,
  defaultDimensions,
  dimension,
  onSearch,
}) => {
  const tempDashboards = useSelector(tempDashboardsSelector) || {};
  const globalFilters = useSelector(filterSelector);
  const globalInterval = useSelector(intervalSelector);
  const { flowId } = useDrilldownMeta();

  const interval = useMemo(() => {
    let from = moment(globalInterval.from);
    let to = moment(globalInterval.to);
    // Check if the difference is more than 30 days
    if (to.diff(from, 'days') > 30) {
      to = moment();
      from = to.clone().subtract(30, 'days');
    }
    return `${moment(from).format()}/${moment(to).format()}`;
  }, [globalInterval]);

  const keys = Object.keys(tempDashboards)
    .filter((key) => /^[0-9]*$/.test(key))
    .map(Number);
  const maxTempId = keys.length === 0 ? 1 : Math.max.apply(null, keys) + 1;

  const handleClientClick = useCallback(
    (eventName, uuid) => {
      const tempFilter = [
        ...globalFilters,
        [
          {
            field: 'event_name',
            key: null,
            op: 'eq',
            value: eventName,
            display: eventName,
            originalField: 'event_name',
            _order: 0,
          },
        ],
      ];
      localStorage.set({
        key: genGlobalFiltersLocalstorageKey(uuid),
        value: tempFilter,
        expires: 7 * 1000 * 60 * 60 * 24, // 7 days
      });
    },
    [globalFilters],
  );

  const titleRenderer = useCallback(
    (dim) => {
      const title =
        ECO_RESERVED_DIMENSION_INFOS[dim]?.name ||
        dimensionList.find((d) => d.groupBy === dim)?.name ||
        dim;
      // search only enable for default and current dimension
      if (![...defaultDimensions, dimension].includes(dim)) {
        return title;
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title}
          <SearchTool
            currentKey={dim}
            columnSearchData={{
              text: '',
              key: dim,
            }}
            onSearch={onSearch}
          />
        </div>
      );
    },
    [onSearch, dimensionList, defaultDimensions, dimension],
  );

  const columns = useMemo(() => {
    const columns: any = [];

    anchoredDimensions.forEach((dim) => {
      columns.push({
        fixed: 'left',
        dataIndex: `event.${dim}`,
        key: `event.${dim}`,
        title: titleRenderer(dim),
        width: 150,
        ellipsis: true,
      });
    });
    columns.push({
      title: titleRenderer('event_name'),
      dataIndex: 'event.event_name',
      key: 'event.event_name',
      fixed: 'left',
      width: 180,
      ellipsis: true,
    });
    columns.push({
      title: titleRenderer('client_id'),
      key: 'event.client_id',
      render: (text, record) => {
        const clientId = record['event.client_id'] || null;
        const eventName = record['event.event_name'] || null;
        // generate unique uuid for each link
        const uuid = uuidv4();

        const link = getAppViewerModuleTempDashboardUrl(
          { clientId, source: 'sampled-table', interval, key: uuid, flowId },
          maxTempId + '',
        );
        return (
          <Tooltip title={clientId} placement="topLeft">
            <TableCellWithCopy>
              <a
                href={link}
                style={{
                  color: '#1890ff',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  flex: 1,
                }}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleClientClick(eventName, uuid)}
                onContextMenu={() => handleClientClick(eventName, uuid)}
              >
                {clientId}
              </a>
              <StyledButton
                type="text"
                icon={<CopyOutlined />}
                className="copy-button"
                onClick={() => {
                  copy(clientId, { format: 'text/plain' });
                  message.info('Copied to clipboard');
                }}
              />
            </TableCellWithCopy>
          </Tooltip>
        );
      },
      fixed: 'left',
      allowCopy: true,
      width: 200,
      ellipsis: true,
    });

    columns.push({
      title: titleRenderer('app_user_id'),
      key: 'event.app_user_id',
      dataIndex: 'event.app_user_id',
      render: (text, record) => {
        const viewerId = record['event.app_user_id'] || null;
        const clientId = record['event.client_id'] || null;
        const eventName = record['event.event_name'] || null;
        const uuid = uuidv4();

        const link = getAppViewerModuleTempDashboardUrl(
          {
            clientId,
            viewerId: viewerId === 'NA' ? null : viewerId,
            source: 'sampled-table',
            interval,
            key: uuid,
            flowId,
          },
          maxTempId + '',
        );
        return (
          <Tooltip title={viewerId} placement="topLeft">
            <TableCellWithCopy>
              <a
                href={link}
                style={{
                  color: '#1890ff',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  flex: 1,
                }}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleClientClick(eventName, uuid)}
                onContextMenu={() => handleClientClick(eventName, uuid)}
              >
                {viewerId}
              </a>
              <StyledButton
                type="text"
                icon={<CopyOutlined />}
                className="copy-button"
                onClick={() => {
                  copy(viewerId, { format: 'text/plain' });
                  message.info('Copied to clipboard');
                }}
              />
            </TableCellWithCopy>
          </Tooltip>
        );
      },
      fixed: 'left',
      width: 200,
      ellipsis: true,
    });
    selectedDimensions.forEach((dim) => {
      if (anchoredDimensions.includes(dim) || defaultDimensions.includes(dim)) {
        return;
      }
      columns.push({
        title: titleRenderer(dim),
        dataIndex: `event.${dim}`,
        key: `event.${dim}`,
        width: 150,
        ellipsis: true,
      });
    });
    return columns;
  }, [
    selectedDimensions,
    anchoredDimensions,
    defaultDimensions,
    maxTempId,
    handleClientClick,
    interval,
    titleRenderer,
  ]);
  return columns;
};
